import { useEffect, useState } from "react";
import { Modal, Box, Button } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import "./errorModal.css";

// Global "permission denied" modal. Listens for `ub:permission_denied`
// events dispatched by the central fetch interceptors (see `src/index.js`
// and `src/utils/api.js`). This is the SINGLE place where read-only /
// forbidden errors surface in the UI. No toasts, no per-page alerts,
// so the experience is consistent across every screen.
//
// The component keeps its original filename to avoid churn in the App
// import, but it now renders a centered MUI modal instead of a toast.
const PermissionToast = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const handler = (e) => {
      const detail = (e?.detail?.message || "").toString();
      const friendly = /only the account owner/i.test(detail)
        ? t("permission_owner_only_message")
        : t("permission_readonly_message");
      setMessage(friendly);
      setOpen(true);
    };
    window.addEventListener("ub:permission_denied", handler);
    return () => window.removeEventListener("ub:permission_denied", handler);
  }, [t]);

  const handleClose = () => setOpen(false);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "white",
          borderRadius: "10px",
          boxShadow: 24,
          p: 4,
          padding: isMobile ? "20px" : "30px",
          width: isMobile ? "80%" : "90%",
          maxWidth: 420,
          outline: "none",
        }}
      >
        <div className="error-modal-header">
          <div className="error-modal-icon">
            <FontAwesomeIcon icon={faCircleExclamation} />
          </div>
          <h2>{t("permission_modal_title")}</h2>
        </div>
        <p className="error-modal-message">{message}</p>
        <div className="error-modal-actions">
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              backgroundColor: "#5616ea",
              "&:hover": { backgroundColor: "#4a14d6" },
              textTransform: "none",
              fontWeight: 500,
              padding: "8px 24px",
            }}
          >
            {t("ok")}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default PermissionToast;
